import * as React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../store/store.ts";
import { FetchAPIData } from "../../utils/GlobalFunctions/fetch.ts";
import {
  formatSeasonLabel,
  sortTvSeasons,
  truncateEpisodeOverview,
  type TvSeasonSummary,
} from "./tvSeasonUtils.ts";

export type TvSeasonsSectionProps = {
  tvId: string | undefined;
  seasons: TvSeasonSummary[];
};

type SeasonDetailResponse = {
  success?: boolean;
  status_message?: string;
  episodes?: Array<{
    id?: number;
    episode_number: number;
    name: string;
    air_date?: string;
    overview?: string;
    still_path?: string | null;
    vote_average?: number;
  }>;
};

const NO_IMG = "/images/no-image.jpg";

const TvSeasonsSection: React.FC<TvSeasonsSectionProps> = ({ tvId, seasons }) => {
  const sessionData = useSelector((state: RootState) => state.session);
  const [cache, setCache] = useState<
    Record<number, SeasonDetailResponse>
  >({});
  const [loadingSeason, setLoadingSeason] = useState<number | null>(null);
  const [errors, setErrors] = useState<Record<number, string>>({});

  const cacheRef = useRef(cache);
  cacheRef.current = cache;
  const inFlightRef = useRef<Set<number>>(new Set());

  const sorted = useMemo(() => sortTvSeasons(seasons), [seasons]);

  useEffect(() => {
    setCache({});
    setErrors({});
    setLoadingSeason(null);
    inFlightRef.current = new Set();
  }, [tvId]);

  const loadSeason = useCallback(
    async (seasonNumber: number) => {
      if (
        !tvId ||
        cacheRef.current[seasonNumber] ||
        inFlightRef.current.has(seasonNumber)
      ) {
        return;
      }
      inFlightRef.current.add(seasonNumber);
      setLoadingSeason(seasonNumber);
      setErrors((prev) => {
        const next = { ...prev };
        delete next[seasonNumber];
        return next;
      });
      try {
        const data: SeasonDetailResponse = await FetchAPIData(
          `tv/${tvId}/season/${seasonNumber}`,
          sessionData,
        );
        if (data.success === false) {
          setErrors((prev) => ({
            ...prev,
            [seasonNumber]:
              data.status_message ||
              "Could not load episodes for this season.",
          }));
          return;
        }
        setCache((prev) => {
          if (prev[seasonNumber]) {
            return prev;
          }
          return { ...prev, [seasonNumber]: data };
        });
      } finally {
        inFlightRef.current.delete(seasonNumber);
        setLoadingSeason(null);
      }
    },
    [tvId, sessionData],
  );

  const onSeasonToggle = useCallback(
    (seasonNumber: number, open: boolean) => {
      if (open) {
        void loadSeason(seasonNumber);
      }
    },
    [loadSeason],
  );

  if (!tvId || !sorted.length) {
    return null;
  }

  return (
    <div className="tv-seasons-section">
      <h3 className="tv-seasons-heading">Seasons</h3>
      <div className="tv-seasons-list">
        {sorted.map((season) => {
          const label = formatSeasonLabel(season);
          const poster =
            season.poster_path && String(season.poster_path).length > 0
              ? `https://image.tmdb.org/t/p/w185${season.poster_path}`
              : NO_IMG;
          const sn = season.season_number;
          const detail = cache[sn];
          const episodes = [...(detail?.episodes ?? [])].sort(
            (a, b) => a.episode_number - b.episode_number,
          );

          return (
            <details
              key={season.id ?? `s-${sn}`}
              className="tv-season-details"
              onToggle={(e) => {
                onSeasonToggle(sn, e.currentTarget.open);
              }}
            >
              <summary className="tv-season-summary">
                <span className="tv-season-summary-inner">
                  <img
                    src={poster}
                    alt=""
                    className="tv-season-poster"
                    width={74}
                    height={111}
                    decoding="async"
                  />
                  <span className="tv-season-meta">
                    <span className="tv-season-title">{label}</span>
                    <span className="tv-season-sub text-muted">
                      {typeof season.episode_count === "number"
                        ? `${season.episode_count} episode${
                            season.episode_count === 1 ? "" : "s"
                          }`
                        : ""}
                      {season.air_date
                        ? `${typeof season.episode_count === "number" ? " · " : ""}${season.air_date}`
                        : ""}
                    </span>
                  </span>
                </span>
              </summary>
              <div className="tv-season-body">
                {errors[sn] && (
                  <p className="tv-season-error" role="alert">
                    {errors[sn]}
                  </p>
                )}
                {loadingSeason === sn && !detail && !errors[sn] && (
                  <p className="tv-season-loading text-muted">
                    Loading episodes…
                  </p>
                )}
                {detail &&
                  !errors[sn] &&
                  episodes.length === 0 &&
                  loadingSeason !== sn && (
                    <p className="tv-season-empty text-muted">
                      No episodes listed for this season.
                    </p>
                  )}
                {episodes.length > 0 && (
                  <ul className="tv-episode-list">
                    {episodes.map((ep) => {
                      const still =
                        ep.still_path && String(ep.still_path).length > 0
                          ? `https://image.tmdb.org/t/p/w300${ep.still_path}`
                          : NO_IMG;
                      const overview = truncateEpisodeOverview(ep.overview);
                      return (
                        <li
                          key={ep.id ?? `e-${sn}-${ep.episode_number}`}
                          className="tv-episode-row"
                        >
                          <img
                            src={still}
                            alt=""
                            className="tv-episode-still"
                            width={120}
                            height={68}
                            decoding="async"
                          />
                          <div className="tv-episode-text">
                            <div className="tv-episode-title-line">
                              <span className="tv-episode-num">
                                E{ep.episode_number}
                              </span>
                              <span className="tv-episode-name">{ep.name}</span>
                              {ep.air_date ? (
                                <span className="tv-episode-date text-muted">
                                  {ep.air_date}
                                </span>
                              ) : null}
                            </div>
                            {overview ? (
                              <p className="tv-episode-overview text-muted">
                                {overview}
                              </p>
                            ) : null}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </details>
          );
        })}
      </div>
    </div>
  );
};

export default TvSeasonsSection;
